import React, { useState, useEffect } from 'react';
import {
  SafetyObjective,
  SafetyIndicator,
  SafetyPerformanceDashboardStats,
  IndicatorType,
} from './types';
import { performanceService } from './performanceService';
import { SPICatalogTable } from './components/SPICatalogTable';
import { ObjectivesOverview } from './components/ObjectivesOverview';
import { SPIDetailModal } from './components/SPIDetailModal';
import { CreateObjectiveModal } from './components/CreateObjectiveModal';
import { CreateSPIModal } from './components/CreateSPIModal';
import { SRBReviewsTab } from './components/SRBReviewsTab';
import { isSmsPersonnel } from '../../../shared/rolePermissions';
import { administrationService, Department } from '../../administration/administrationService';
import { Users, UserPlus, Trash2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useTableSort, SortableTh } from '../../../shared/useTableSort';

export const PerformancePage: React.FC = () => {
  const [currentUser] = useState<any>(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const isSmsUser = isSmsPersonnel(
    currentUser?.user_type || currentUser?.role,
    currentUser?.department
  );

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const availableYears = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4];

  const [activeTab, setActiveTab] = useState<'SPIS' | 'OBJECTIVES' | 'SRB' | 'DELEGATES'>('SPIS');
  const [filterType, setFilterType] = useState<'ALL' | IndicatorType>('ALL');

  const [stats, setStats] = useState<SafetyPerformanceDashboardStats | null>(null);
  const [indicators, setIndicators] = useState<SafetyIndicator[]>([]);
  const [objectives, setObjectives] = useState<SafetyObjective[]>([]);
  const [loading, setLoading] = useState(false);

  // Department Delegation & Focal Points (Item 12)
  const [deptList, setDeptList] = useState<Department[]>([]);
  const [selectedDeptForDelegates, setSelectedDeptForDelegates] = useState<string>('Flight Operations');
  const [deptDelegates, setDeptDelegates] = useState<any[]>([]);
  const { sortedItems: sortedDeptDelegates, sortField: delSortField, sortDirection: delSortDir, toggleSort: toggleDelSort } = useTableSort(deptDelegates);
  const [loadingDelegates, setLoadingDelegates] = useState<boolean>(false);
  const [newDelegateUserId, setNewDelegateUserId] = useState<string>('');
  const [newDelegateUserName, setNewDelegateUserName] = useState<string>('');
  const [newDelegateRoleTitle, setNewDelegateRoleTitle] = useState<string>('SPI_FOCAL_POINT');

  // Modals
  const [selectedIndicator, setSelectedIndicator] = useState<SafetyIndicator | null>(null);
  const [showCreateObjective, setShowCreateObjective] = useState(false);
  const [showCreateSPI, setShowCreateSPI] = useState(false);

  useEffect(() => {
    loadAllData(selectedYear);
  }, [selectedYear]);

  // Read URL search params for direct indicator deep-linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const indId = params.get('indicatorId');
    if (indId && indicators.length > 0) {
      const found = indicators.find((i) => i.id === indId || i.code === indId);
      if (found) {
        setSelectedIndicator(found);
      }
    }
  }, [indicators]);

  // Load departments for delegation
  useEffect(() => {
    administrationService.getDepartments().then((depts) => {
      const active = depts.filter((d) => d.status === 'ACTIVE');
      setDeptList(active);
      if (active.length > 0 && !selectedDeptForDelegates) {
        setSelectedDeptForDelegates(active[0].name);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedDeptForDelegates && activeTab === 'DELEGATES') {
      loadDeptDelegates(selectedDeptForDelegates);
    }
  }, [selectedDeptForDelegates, activeTab]);

  const loadDeptDelegates = async (dept: string) => {
    try {
      setLoadingDelegates(true);
      const list = await performanceService.getDepartmentSpiAuthorizedUsers(dept);
      setDeptDelegates(list);
    } catch (err) {
      console.error('Failed to load department delegates:', err);
    } finally {
      setLoadingDelegates(false);
    }
  };

  const handleGrantDelegate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDelegateUserId.trim() || !selectedDeptForDelegates) return;
    try {
      await performanceService.grantDepartmentSpiRole(selectedDeptForDelegates, {
        userId: newDelegateUserId.trim(),
        userName: newDelegateUserName.trim() || undefined,
        roleTitle: newDelegateRoleTitle.trim() || 'SPI_FOCAL_POINT',
      });
      setNewDelegateUserId('');
      setNewDelegateUserName('');
      await loadDeptDelegates(selectedDeptForDelegates);
      alert('✓ SPI Focal Point role granted successfully.');
    } catch (err: any) {
      alert(`Failed to grant role: ${err.message || 'Unknown error'}`);
    }
  };

  const handleRevokeDelegate = async (userId: string) => {
    if (!confirm(`Revoke SPI access for user ${userId} in ${selectedDeptForDelegates}?`)) return;
    try {
      await performanceService.revokeDepartmentSpiRole(selectedDeptForDelegates, userId);
      await loadDeptDelegates(selectedDeptForDelegates);
      alert('✓ Access revoked.');
    } catch (err: any) {
      alert(`Failed to revoke access: ${err.message || 'Unknown error'}`);
    }
  };

  const loadAllData = async (targetYear: number = selectedYear) => {
    try {
      setLoading(true);
      const [statsData, indsData, objsData] = await Promise.all([
        performanceService.getDashboardStats(undefined, targetYear).catch(() => null),
        performanceService.getIndicators({ year: targetYear }).catch(() => []),
        performanceService.getObjectives().catch(() => []),
      ]);
      const safeInds = Array.isArray(indsData) ? indsData : [];
      const safeObjs = Array.isArray(objsData) ? objsData : [];

      let calcNormal = 0;
      let calcAlert1 = 0;
      let calcAlert2 = 0;
      let calcAlert3 = 0;

      for (const ind of safeInds) {
        const latestVal = ind.currentValue ?? (ind.recentValues && ind.recentValues.length > 0 ? ind.recentValues[ind.recentValues.length - 1] : undefined);
        const lvl = String(latestVal?.alertLevel || '').trim().toUpperCase();
        if (lvl === 'ALERT_1_SIGMA' || lvl === 'CAUTION' || lvl === 'AMBER' || lvl === 'YELLOW') {
          calcAlert1++;
        } else if (lvl === 'ALERT_2_SIGMA' || lvl === 'WARNING' || lvl === 'ORANGE') {
          calcAlert2++;
        } else if (lvl === 'ALERT_3_SIGMA' || lvl === 'BREACH' || lvl === 'CRITICAL' || lvl === 'RED' || lvl === 'ALARM') {
          calcAlert3++;
        } else {
          calcNormal++;
        }
      }

      const totalInds = statsData?.totalIndicators ?? (statsData as any)?.totalSPIs ?? safeInds.length;
      const leadingCount = statsData?.leadingIndicatorsCount ?? (statsData as any)?.leadingCount ?? safeInds.filter((i) => i.indicatorType === 'LEADING').length;
      const laggingCount = statsData?.laggingIndicatorsCount ?? (statsData as any)?.laggingCount ?? safeInds.filter((i) => i.indicatorType === 'LAGGING').length;
      const totalObjs = statsData?.totalObjectives ?? safeObjs.length;
      const normalCount = statsData?.normalStatusCount ?? calcNormal;
      const alert1 = statsData?.alert1Count ?? calcAlert1;
      const alert2 = statsData?.alert2Count ?? calcAlert2;
      const alert3 = statsData?.alert3Count ?? (statsData as any)?.activeBreachesCount ?? calcAlert3;

      const mergedStats: SafetyPerformanceDashboardStats = {
        totalObjectives: totalObjs,
        totalIndicators: totalInds,
        leadingIndicatorsCount: leadingCount,
        laggingIndicatorsCount: laggingCount,
        normalStatusCount: normalCount,
        alert1Count: alert1,
        alert2Count: alert2,
        alert3Count: alert3,
        ...(statsData || {}),
      };

      setStats(mergedStats);
      setIndicators(safeInds);
      setObjectives(safeObjs);
    } catch (err) {
      console.error('Error loading safety performance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const userDept = currentUser?.department?.trim().toLowerCase();
  const myProviderCount = indicators.filter(
    (i) => Boolean(userDept) && (i.dataProviderDepartment || i.ownerDepartment || i.department)?.trim().toLowerCase() === userDept
  ).length;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Title & Header with Year Selector */}
      <div className="page-header-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div className="badge">SAFETY MANAGEMENT</div>
          <h1>Safety Performance Monitoring (SPI & SPT)</h1>
          <p>ICAO Annex 19 & EASA SMS safety objectives, SPI/SPT indicators, and 1σ/2σ/3σ threshold alerting.</p>
        </div>

        {/* Global Year Switcher / History Filter */}
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 16px', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Performance Year
            </div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>{selectedYear}</span>
              {selectedYear === currentYear ? (
                <span style={{ fontSize: '11px', fontWeight: 700, backgroundColor: '#dcfce7', color: '#15803d', padding: '1px 7px', borderRadius: '4px' }}>
                  Current Year
                </span>
              ) : selectedYear < currentYear ? (
                <span style={{ fontSize: '11px', fontWeight: 700, backgroundColor: '#f1f5f9', color: '#475569', padding: '1px 7px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                  Historical ({selectedYear})
                </span>
              ) : (
                <span style={{ fontSize: '11px', fontWeight: 700, backgroundColor: '#e0e7ff', color: '#4338ca', padding: '1px 7px', borderRadius: '4px' }}>
                  Future ({selectedYear})
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              type="button"
              onClick={() => setSelectedYear((prev) => prev - 1)}
              title="Navigate to Previous Year"
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                fontWeight: 700,
                backgroundColor: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#334155',
              }}
            >
              ◄
            </button>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: 700,
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#0f172a',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {availableYears.map((yr) => (
                <option key={yr} value={yr}>
                  Year {yr} {yr === currentYear ? '★ (Active)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSelectedYear((prev) => prev + 1)}
              title="Navigate to Next Year"
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                fontWeight: 700,
                backgroundColor: '#f1f5f9',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#334155',
              }}
            >
              ►
            </button>
          </div>
        </div>
      </div>

      {/* Data Provider Responsibility Alert Banner */}
      {myProviderCount > 0 && !isSmsUser && (
        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>⚡</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
                Data Provider Responsibility: {currentUser?.department}
              </div>
              <div style={{ fontSize: '12px', color: '#b45309' }}>
                Your department is designated as the responsible Data Provider for {myProviderCount} SPI{myProviderCount > 1 ? 's' : ''}. You have direct data entry permissions to input and update monthly indicator values.
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setActiveTab('SPIS');
            }}
            className="btn-primary"
            style={{ backgroundColor: '#d97706', borderColor: '#b45309', fontSize: '12px', width: 'auto', padding: '6px 14px' }}
          >
            Go to My Data Entry Queue ({myProviderCount}) →
          </button>
        </div>
      )}

      {/* KPI Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Objectives</div>
            <div className="stat-value">{stats.totalObjectives}</div>
            <div className="stat-sub">Strategic & Operational</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Total SPIs</div>
            <div className="stat-value" style={{ color: '#0284c7' }}>{(stats.totalIndicators ?? (stats as any).totalSPIs ?? 0)}</div>
            <div className="stat-sub">
              {(stats.leadingIndicatorsCount ?? (stats as any).leadingCount ?? 0)} Leading / {(stats.laggingIndicatorsCount ?? (stats as any).laggingCount ?? 0)} Lagging
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-label">Under Control</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{(stats.normalStatusCount ?? 0)}</div>
            <div className="stat-sub">Within limits</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">1σ (Caution)</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{(stats.alert1Count ?? 0)}</div>
            <div className="stat-sub">1-Sigma deviation</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">2σ (Warning)</div>
            <div className="stat-value" style={{ color: '#ea580c' }}>{(stats.alert2Count ?? 0)}</div>
            <div className="stat-sub">2-Sigma deviation</div>
          </div>

          <div className="stat-card">
            <div className="stat-label">3σ (Critical Alarm)</div>
            <div className="stat-value" style={{ color: '#dc2626' }}>{(stats.alert3Count ?? (stats as any).activeBreachesCount ?? 0)}</div>
            <div className="stat-sub">Immediate action required</div>
          </div>
        </div>
      )}

      {/* Main Tabs Navigation */}
      <div className="nav-tabs">
        <button
          onClick={() => setActiveTab('SPIS')}
          className={`tab-btn ${activeTab === 'SPIS' ? 'active' : ''}`}
        >
          Safety Indicators (SPIs) ({indicators.length})
        </button>
        <button
          onClick={() => setActiveTab('OBJECTIVES')}
          className={`tab-btn ${activeTab === 'OBJECTIVES' ? 'active' : ''}`}
        >
          Safety Objectives ({objectives.length})
        </button>
        <button
          onClick={() => setActiveTab('SRB')}
          className={`tab-btn ${activeTab === 'SRB' ? 'active' : ''}`}
        >
          Safety Review Board (SRB) & Snapshots
        </button>
        {isSmsUser && (
          <button
            onClick={() => setActiveTab('DELEGATES')}
            className={`tab-btn ${activeTab === 'DELEGATES' ? 'active' : ''}`}
          >
            👥 Department SPI Focal Points & Access (Item 12)
          </button>
        )}
      </div>

      {/* Tab Panels */}
      {activeTab === 'SPIS' && (
        <SPICatalogTable
          indicators={indicators}
          loading={loading}
          filterType={filterType}
          onFilterTypeChange={setFilterType}
          onOpenCreate={() => setShowCreateSPI(true)}
          onSelectIndicator={(ind) => setSelectedIndicator(ind)}
          isSmsUser={isSmsUser}
          currentUserDept={currentUser?.department}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
      )}

      {activeTab === 'OBJECTIVES' && (
        <ObjectivesOverview
          objectives={objectives}
          loading={loading}
          onOpenCreate={() => setShowCreateObjective(true)}
          onSelectObjective={(objId) => {
            // Filter SPIs by objective and switch to SPI tab
            setActiveTab('SPIS');
          }}
        />
      )}

      {activeTab === 'SRB' && <SRBReviewsTab />}

      {activeTab === 'DELEGATES' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ShieldCheck size={22} color="#2563eb" />
                  Department SPI Focal Points & Role Delegation
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
                  Aviation SMS Rule (Item 12): Only designated focal points within a department may access and manage departmental SPIs. SMS personnel and Admins grant specific user authority here.
                </p>
              </div>

              {/* Department Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                  Target Department:
                </label>
                <select
                  value={selectedDeptForDelegates}
                  onChange={(e) => setSelectedDeptForDelegates(e.target.value)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    fontWeight: 600,
                    background: '#ffffff',
                    color: '#0f172a'
                  }}
                >
                  {deptList.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name} ({d.department_code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Grant Form */}
            <form onSubmit={handleGrantDelegate} style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                  User ID / Username *
                </label>
                <input
                  type="text"
                  required
                  value={newDelegateUserId}
                  onChange={(e) => setNewDelegateUserId(e.target.value)}
                  placeholder="e.g. usr_selim_kaya or selim.kaya"
                  style={{ width: '100%', padding: '7px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                  Display Name (Optional)
                </label>
                <input
                  type="text"
                  value={newDelegateUserName}
                  onChange={(e) => setNewDelegateUserName(e.target.value)}
                  placeholder="e.g. Capt. Selim Kaya (Flight Ops Lead)"
                  style={{ width: '100%', padding: '7px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div style={{ flex: '1 1 160px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', marginBottom: '4px' }}>
                  Delegated SPI Role Title
                </label>
                <input
                  type="text"
                  value={newDelegateRoleTitle}
                  onChange={(e) => setNewDelegateRoleTitle(e.target.value)}
                  placeholder="e.g. SPI_FOCAL_POINT or DATA_PROVIDER"
                  style={{ width: '100%', padding: '7px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <button
                type="submit"
                className="btn-primary"
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: '35px'
                }}
              >
                <UserPlus size={14} />
                <span>Authorize Focal Point</span>
              </button>
            </form>

            {/* Current Authorized Users Table */}
            <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', margin: '0 0 12px 0' }}>
              Authorized SPI Personnel for {selectedDeptForDelegates}
            </h4>

            <div className="table-container" style={{ margin: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableTh field="user_id" currentField={delSortField} currentDirection={delSortDir} onSort={toggleDelSort}>
                      User ID
                    </SortableTh>
                    <SortableTh field="user_name" currentField={delSortField} currentDirection={delSortDir} onSort={toggleDelSort}>
                      User Name
                    </SortableTh>
                    <SortableTh field="role_title" currentField={delSortField} currentDirection={delSortDir} onSort={toggleDelSort}>
                      Role Title
                    </SortableTh>
                    <SortableTh field="granted_by" currentField={delSortField} currentDirection={delSortDir} onSort={toggleDelSort}>
                      Granted By
                    </SortableTh>
                    <SortableTh field="created_at" currentField={delSortField} currentDirection={delSortDir} onSort={toggleDelSort}>
                      Granted Date
                    </SortableTh>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingDelegates ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                        Loading authorized department delegates...
                      </td>
                    </tr>
                  ) : sortedDeptDelegates.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                        No specific focal points configured for {selectedDeptForDelegates}. Default department membership applies.
                      </td>
                    </tr>
                  ) : (
                    sortedDeptDelegates.map((u) => (
                      <tr key={u.user_id}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                          {u.user_id}
                        </td>
                        <td style={{ fontWeight: 600 }}>{u.user_name || '—'}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', background: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '11px' }}>
                            {u.role_title || 'SPI_FOCAL_POINT'}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>{u.granted_by || 'SMS_MANAGER'}</td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleRevokeDelegate(u.user_id)}
                            style={{
                              padding: '4px 10px',
                              fontSize: '11px',
                              fontWeight: 600,
                              borderRadius: '4px',
                              border: '1px solid #fecdd3',
                              background: '#fff1f2',
                              color: '#dc2626',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Trash2 size={12} />
                            <span>Revoke</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedIndicator && (
        <SPIDetailModal
          indicator={selectedIndicator}
          onClose={() => setSelectedIndicator(null)}
          onRefresh={() => loadAllData(selectedYear)}
          isSmsUser={isSmsUser}
          currentUserDept={currentUser?.department}
          selectedYear={selectedYear}
        />
      )}

      {showCreateObjective && (
        <CreateObjectiveModal
          onClose={() => setShowCreateObjective(false)}
          onSuccess={() => {
            setShowCreateObjective(false);
            loadAllData(selectedYear);
          }}
        />
      )}

      {showCreateSPI && (
        <CreateSPIModal
          objectives={objectives}
          onClose={() => setShowCreateSPI(false)}
          onSuccess={() => {
            setShowCreateSPI(false);
            loadAllData(selectedYear);
          }}
        />
      )}
    </div>
  );
};
export default PerformancePage;
